import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { CALL_OUTCOME_LABELS, type CallOutcome } from '@salesbee/contracts';
import { Badge, Button, Card, CardContent, EmptyState, ErrorState, LoadingState, cn, formatMoney } from '@salesbee/ui';
import { trpc } from '../lib/trpc';

export type PlanFeature = 'crm' | 'pipeline' | 'prospecting' | 'outbound_calling' | 'dnc_management' | 'sequences' | 'crm_sync' | 'api';

export const FEATURE_LABELS: Record<PlanFeature, string> = {
  crm: 'Contacts and companies',
  pipeline: 'Deal pipeline',
  prospecting: 'Prospecting',
  outbound_calling: 'AI outbound calling',
  dnc_management: 'Do-not-call management',
  sequences: 'Multi-step sequences',
  crm_sync: 'CRM sync',
  api: 'API keys and webhooks',
};

export function useCurrent() {
  return trpc.workspace.current.useQuery(undefined, { staleTime: 30_000 });
}

export function money(cents: number | null | undefined, currency = 'CAD') {
  return formatMoney(cents ?? 0, currency);
}

/** "Upgrade" prompt for a plan-gated feature; Hive-managed workspaces upgrade in Hive. */
export function UpgradePrompt({ feature, compact }: { feature: PlanFeature; compact?: boolean }) {
  const current = useCurrent();
  const hive = current.data?.hive;
  const action = current.data?.hiveManaged ? (
    hive?.billingUrl ? (
      <Button asChild>
        <a href={hive.billingUrl}>Upgrade in Hive</a>
      </Button>
    ) : (
      <span className="text-sm text-muted-foreground">Upgrade in Hive</span>
    )
  ) : (
    <Button asChild>
      <Link to="/settings/billing">Upgrade to Growth</Link>
    </Button>
  );
  if (compact) return action;
  return (
    <Card>
      <CardContent className="py-2">
        <EmptyState
          icon={<Lock />}
          title={`${FEATURE_LABELS[feature]} is part of SalesBee Growth`}
          description="Growth adds multi-step sequences, CRM sync, API keys and webhooks, 1,000 outbound minutes and 2,000 prospects a month."
          action={action}
        />
      </CardContent>
    </Card>
  );
}

/** Render children only when the plan includes `feature`; otherwise the upgrade prompt. */
export function FeatureGate({ feature, children }: { feature: PlanFeature; children: ReactNode }) {
  const current = useCurrent();
  if (current.isLoading) return <LoadingState />;
  const ent = current.data?.entitlements;
  if (ent && ent.active && !ent.features.includes(feature)) return <UpgradePrompt feature={feature} />;
  return <>{children}</>;
}

export function Stat({ label, value, hint, className }: { label: string; value: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardContent className="space-y-1 py-5">
        <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        <p className="font-mono text-[26px] font-semibold tracking-tight">{value}</p>
        {hint ? <p className="text-[13px] text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export function Meter({ label, used, limit, unit }: { label: string; used: number; limit: number | null; unit: string }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="space-y-1.5" aria-label={label}>
      <div className="flex items-baseline justify-between text-[13px]">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-muted-foreground">
          {used.toLocaleString('en-CA')} / {limit === null ? 'unlimited' : limit.toLocaleString('en-CA')} {unit}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={cn('h-full rounded-full', pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-warning' : 'bg-primary')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const OUTCOME_VARIANT: Partial<Record<CallOutcome, 'success' | 'warning' | 'destructive' | 'info' | 'primary' | 'default'>> = {
  meeting_booked: 'success',
  interested: 'primary',
  connected: 'info',
  callback_requested: 'info',
  not_interested: 'default',
  do_not_call: 'destructive',
  failed: 'destructive',
  voicemail: 'warning',
  no_answer: 'default',
  busy: 'default',
};

export function OutcomeBadge({ outcome }: { outcome: string | null | undefined }) {
  if (!outcome) return <Badge variant="outline">In progress</Badge>;
  return <Badge variant={OUTCOME_VARIANT[outcome as CallOutcome] ?? 'default'}>{CALL_OUTCOME_LABELS[outcome as CallOutcome] ?? outcome}</Badge>;
}

/** Loading / error / content wrapper for one query. */
export function QueryView<T>({ query, children, label }: { query: { isLoading: boolean; error: unknown; data: T | undefined; refetch: () => unknown }; children: (data: T) => ReactNode; label?: string }) {
  if (query.isLoading) return <LoadingState label={label} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.data === undefined) return <LoadingState label={label} />;
  return <>{children(query.data)}</>;
}

export function Section({ title, description, actions, children }: { title: string; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-[18px] font-semibold tracking-tight">{title}</h2>
          {description ? <p className="text-[14px] text-muted-foreground">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
